import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import Icon from "../../components/AppIcon";
import Logo from "./components/Logo";
import FormInput from "./components/FormInput";
import ActionButton from "./components/ActionButton";
import ErrorMessage from "./components/ErrorMessage";
import axios from "axios";
import { API_ENDPOINTS } from "config/config";

const LoginScreen = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [loginError, setLoginError] = useState("");

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });

    if (errors[name]) {
      setErrors({ ...errors, [name]: "" });
    }

    if (loginError) {
      setLoginError("");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    setIsLoading(true);
    setLoginError("");

    try {
      const response = await axios.post(API_ENDPOINTS.LOGIN, {
        email: formData.email,
        password: formData.password,
      });

      const token = response.data.data.token.token;

      if (response.status === 200) {
        localStorage.setItem("authToken", token);
        localStorage.setItem("isAdmin", true);
        navigate("/analytics/overview");
      }
    } catch (err) {
      if (err.response) {
        const status = err.response.status;

        if (status === 401 || status === 400) {
          setLoginError("Invalid email or password.");
        } else if (err.response.data?.message) {
          setLoginError(err.response.data.message);
        } else {
          setLoginError("Something went wrong. Please try again later.");
        }
      } else {
        setLoginError("Unable to connect to the server. Please check your internet connection.");
      }
    } finally {
      setIsLoading(false);
    }
  };


  return (
    <div className="min-h-screen flex flex-col md:flex-row items-center justify-center bg-background">
      {/* Left branding section with improved mobile responsiveness */}
      <div className="hidden md:flex md:w-1/2 bg-primary-700 text-white p-4 lg:p-8 flex-col justify-between h-screen overflow-hidden">
        <div className="max-w-md mx-auto mt-16 lg:mt-32">
          <h1 className="text-2xl lg:text-4xl font-bold mb-4 lg:mb-6">Welcome to Best 1 Admin</h1>
          <p className="text-base lg:text-xl text-primary-100 mb-0">
            Take full control of your business finances with powerful tools designed to simplify tracking, reporting, and management.
          </p>
          <div className="bg-primary-600 bg-opacity-30 p-4 lg:p-6 rounded-lg border border-primary-500 border-opacity-30 backdrop-blur-sm mt-4 lg:mt-6">
            <h2 className="text-sm lg:text-base font-semibold text-primary-200 mb-2 lg:mb-3">Key Features:</h2>
            {[
              "Profit & Expense Tracking: Monitor income, expenses, and profitability in real time.",
              "Business Dashboard: View all business details with dynamic charts and tables.",
              "Advanced Filters: Quickly search, sort, and filter data for deeper insights.",
              "Reports & Downloads: Generate and export detailed financial reports instantly.",
              "Stripe Payments: Manage secure and seamless online transactions."
            ].map((text, i) => {
              const [title, desc] = text.split(":");
              return (
                <div className="flex items-start mb-3 lg:mb-4" key={i}>
                  <div className="mr-2 lg:mr-3 mt-1">
                    <Icon name="CheckCircle" className="text-primary-300 w-4 h-4 lg:w-5 lg:h-5" />
                  </div>
                  <p className="text-sm lg:text-base text-primary-100">
                    <span className="font-semibold">{title}:</span>{desc}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
        <div className="text-primary-200 mt-4 lg:mt-6 text-xs lg:text-sm">
          &copy; {new Date().getFullYear()} Best 1 Admin. All rights reserved.
        </div>
      </div>


      {/* Right side - Two Step Form */}
      <div className="flex-1 flex items-center justify-center p-6 md:p-10">
        <div className="w-full max-w-md">
          <div className="md:hidden mb-8">
            <div className="flex items-center">
              <div className="text-primary-600 w-12 h-12 bg-primary-600">
                <img
                  src="/tmpj5uq7cal.webp"
                  alt="Logo"
                  className="w-full h-full"
                />
              </div>
              <span className="ml-8 text-xl font-bold text-gray-900">
                Best Version 1
              </span>
            </div>
          </div>

          <div className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Sign in to your account</h2>
            <p className="text-gray-600">
              Enter your email and password
            </p>
          </div>

          {loginError && (
            <ErrorMessage message={loginError} onClose={() => setLoginError("")} />
          )}

          <form onSubmit={handleSubmit} className="space-y-6" noValidate>

            <FormInput
              label="Email"
              type="email"
              name="email"
              id="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="Enter your email"
              error={errors.phone}
              // autoComplete="tel"
              required
            />

            <FormInput
              label="Password"
              type="password"
              name="password"
              id="password"
              value={formData.password}
              onChange={handleChange}
              placeholder="Enter your password"
              error={errors.password}
              // autoComplete="one-time-code"
              required
            />

            <ActionButton
              type="submit"
              fullWidth
              isLoading={isLoading}
              disabled={isLoading || !formData.email || !formData.password}
            >
              Sign in
            </ActionButton>
          </form>

          <div className="mt-8 text-center text-sm text-gray-500 md:hidden">
            &copy; {new Date().getFullYear()} Best Version 1 Admin. All rights reserved.
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginScreen;